import { useState } from "react";
import { Shield, Sword, Sparkles, ChevronDown, Coins, Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// V.032 — Icon glyph per role. `card_roles.py` stores an icon name — we map it
// here so backend copy stays canonical.
const ROLE_ICONS = { shield: Shield, sword: Sword, sparkles: Sparkles };

// Fallback role palette used when the card doc predates the roles migration and
// the backfill hasn't yet run — keeps the badge visually stable during the flip.
const FALLBACK_ROLE = {
  tank: { key: "tank", name: "Tank", color: "#38bdf8", icon: "shield",
    ability: { name: "Bastion", desc: "Reduces incoming damage by 15% and gains +25% max HP in Arena." } },
  mauler: { key: "mauler", name: "Mauler", color: "#fb7185", icon: "sword",
    ability: { name: "Berserker", desc: "Deals +20% damage; when below 50% HP, damage bonus doubles to +40%." } },
  assist: { key: "assist", name: "Assist", color: "#34d399", icon: "sparkles",
    ability: { name: "Conduit", desc: "+20% speed; every 3rd turn cleanses debuffs and restores 10% max HP." } },
};

function resolveRole(card) {
  if (!card) return null;
  if (card.role) return card.role;
  if (card.role_key && FALLBACK_ROLE[card.role_key]) return FALLBACK_ROLE[card.role_key];
  return null;
}

/**
 * Compact colored chip showing a card's primary role + ability tooltip.
 * When `interactive` is true and the viewer is the owner, clicking opens a
 * dropdown to switch roles (which re-rolls arena stats for 1 credit).
 */
export function CardRoleBadge({
  card,
  interactive = false,
  size = "md",              // 'sm' | 'md'
  className = "",
  onChanged,                // called with the updated card after a successful reroll
}) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const role = resolveRole(card);
  const Icon = ROLE_ICONS[role?.icon] || Sparkles;
  const isSm = size === "sm";

  const { data: catalog } = useQuery({
    queryKey: ["cards-roles-catalog"],
    queryFn: async () => (await api.get("/cards/roles/catalog")).data,
    enabled: Boolean(interactive && open),
    staleTime: 5 * 60 * 1000,
  });

  const rerollMut = useMutation({
    mutationFn: async (roleKey) =>
      (await api.post(`/cards/${card?.id}/role`, { role_key: roleKey })).data,
    onSuccess: (res) => {
      toast.success(`Rerolled → ${res?.card?.role?.name}. Fresh stats locked in.`);
      // Invalidate every collection surface that shows cards so the badge and
      // stat chips refresh in place.
      ["my-cards", "card", "hub", "squad", "battle-state", "cavern-state",
       "deep-dive", "trade-builder-collection", "explore"]
        .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ["card", card?.id] });
      onChanged?.(res.card);
      setOpen(false);
    },
    onError: (err) => toast.error(apiError(err, "Couldn't change role")),
  });

  if (!role) return null;

  const chip = (
    <div
      data-testid={`card-role-badge-${card.id}`}
      onPointerDown={interactive ? (e) => e.stopPropagation() : undefined}
      onClick={interactive ? (e) => e.stopPropagation() : undefined}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-mono uppercase tracking-[0.25em] backdrop-blur-sm",
        isSm ? "px-1.5 py-0.5 text-[8px]" : "px-2 py-1 text-[10px]",
        interactive && "cursor-pointer hover:brightness-125 transition",
        className
      )}
      style={{
        color: role.color,
        borderColor: `${role.color}66`,
        background: `${role.color}18`,
      }}
      title={role.ability?.desc}
    >
      <Icon className={isSm ? "h-2.5 w-2.5" : "h-3 w-3"} />
      <span>{role.name}</span>
      {interactive && <ChevronDown className={isSm ? "h-2.5 w-2.5 opacity-70" : "h-3 w-3 opacity-70"} />}
    </div>
  );

  if (!interactive) return chip;

  const cost = catalog?.reroll_cost ?? 1;
  const roles = catalog?.roles || Object.values(FALLBACK_ROLE);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild data-testid={`role-picker-trigger-${card.id}`}>
        {chip}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 bg-zinc-950 border-white/10 text-white">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.3em] text-white/50">
          Primary Role
        </DropdownMenuLabel>
        <div className="px-2 pb-1.5 text-[11px] text-white/60 leading-snug">
          Picking a role costs <span className="text-amber-300 font-semibold">
          <Coins className="inline h-3 w-3 mb-0.5" /> {cost}c</span> and re-rolls this card&apos;s base stats.
        </div>
        <DropdownMenuSeparator className="bg-white/10" />
        {roles.map((r) => {
          const RIcon = ROLE_ICONS[r.icon] || Sparkles;
          const active = r.key === role.key;
          return (
            <DropdownMenuItem
              key={r.key}
              data-testid={`role-option-${r.key}`}
              disabled={rerollMut.isPending}
              onSelect={(e) => { e.preventDefault(); rerollMut.mutate(r.key); }}
              className={cn("flex flex-col items-start gap-1 py-2 focus:bg-white/5",
                active && "bg-white/[0.04]")}
            >
              <div className="flex w-full items-center gap-2">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-md shrink-0"
                  style={{ color: r.color, background: `${r.color}22`, border: `1px solid ${r.color}55` }}
                >
                  <RIcon className="h-3.5 w-3.5" />
                </span>
                <span className="font-mono font-semibold text-[13px]" style={{ color: r.color }}>{r.name}</span>
                {active && (
                  <span className="ml-auto text-[9px] uppercase tracking-wider text-white/50">
                    {rerollMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "current · reroll"}
                  </span>
                )}
              </div>
              <div className="pl-8 text-[11px] text-white/70 leading-snug">
                <span className="text-white/90 font-semibold">{r.ability?.name}</span> — {r.ability?.desc}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Full-width role summary panel used inside ArenaProfile / stats surfaces. */
export function CardRoleRow({ card, interactive = false, onChanged }) {
  const role = resolveRole(card);
  if (!role) return null;
  const Icon = ROLE_ICONS[role.icon] || Sparkles;
  return (
    <div
      data-testid={`card-role-row-${card.id}`}
      className="relative overflow-hidden rounded-lg border px-2.5 py-1.5"
      style={{
        borderColor: `${role.color}55`,
        background: `linear-gradient(90deg, ${role.color}22, ${role.color}0a 45%, transparent)`,
      }}
    >
      <span
        className="absolute left-0 top-0 h-full w-[3px]"
        style={{ background: role.color, boxShadow: `0 0 10px ${role.color}` }}
      />
      <div className="flex items-center gap-2">
        <span
          className="flex h-5 w-5 items-center justify-center rounded-md shrink-0"
          style={{ color: role.color, background: `${role.color}22`, border: `1px solid ${role.color}55` }}
        >
          <Icon className="h-3 w-3" />
        </span>
        <span className="font-mono font-semibold text-[13px] leading-none" style={{ color: role.color }}>
          {role.name}
        </span>
        <span className="ml-auto">
          <CardRoleBadge card={card} interactive={interactive} size="sm" onChanged={onChanged} />
        </span>
      </div>
      {role.ability?.desc && (
        <div className="text-white/70 text-[11px] leading-snug mt-1 pl-7">
          <span className="text-white/90 font-semibold">{role.ability?.name}</span> — {role.ability.desc}
        </div>
      )}
    </div>
  );
}
